import { useSettingQuery } from '@features/setting/queries';
import { useLocation, useNavigate } from 'react-router';
import {
  TextField,
  Button,
  Switch,
  FormControlLabel,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Box,
  Stack,
  Paper,
  Checkbox,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useState, useEffect } from 'react';
import { useCreateSetting, useDeleteSetting, useUpdateSetting } from '@features/setting/mutation';
import { SettingStatus, valueTypeOptions } from '@models/setting';
import { DateFormats, TimeZone, utcStringToLocalDate } from '@utils/date';

import { RenderSettingFieldFromConfig } from '@components/Setting/RenderSettingField';
import { useToast } from '@hooks/useToast';
import { format } from 'date-fns-tz';

function SettingEditPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const settingId = location.pathname.split('/')[3];
  const { data: setting, isLoading: isLoadingSetting } = useSettingQuery(settingId);
  const { mutate: updateSetting } = useUpdateSetting();
  const { mutate: createSetting } = useCreateSetting();
  const { mutate: deleteSetting } = useDeleteSetting();

  const [form, setForm] = useState({
    title: '',
    description: '',
    activeFrom: null as Date | null,
    isSensitive: false,
    valueType: '',
    value: null as string | number | boolean | object | Date | null,
    slug: '',
    createNewWithActiveTime: false,
  });

  useEffect(() => {
    if (setting) {
      setForm({
        title: setting.title || '',
        description: setting.description || '',
        activeFrom: setting.activeFrom
          ? utcStringToLocalDate(setting?.activeFrom?.toString())
          : null,
        isSensitive: setting.isSensitive || false,
        valueType: setting.setting?.type || '',
        value:
          setting.setting?.value === undefined || setting.setting?.value === null
            ? ''
            : typeof setting.setting?.value === 'object'
              ? setting.setting.value
              : String(setting.setting.value),
        slug: setting.slug || '',
        createNewWithActiveTime: false,
      });
    }
  }, [setting]);

  const handleChange =
    (field: keyof typeof form) =>
    (
      event:
        | React.ChangeEvent<HTMLInputElement | { value: unknown }>
        | import('@mui/material/Select').SelectChangeEvent,
    ) => {
      setForm({
        ...form,
        [field]:
          field === 'isSensitive' ? (event.target as HTMLInputElement).checked : event.target.value,
      });
    };

  const handleDateChange = (date: Date | null) => {
    setForm({ ...form, activeFrom: date });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!form.activeFrom || !form.value) {
      toast.error('Active From and Value are required');
      return;
    }

    let convertedValue: string | number | object | Date | boolean = form.value;

    switch (form.valueType) {
      case 'number':
        convertedValue = Number(form.value);
        break;
      case 'json':
        try {
          convertedValue =
            form.value === '' || form.value === null
              ? null
              : typeof form.value === 'object'
                ? form.value
                : JSON.parse(form.value as string);
        } catch {
          toast.error('Invalid JSON value');
          throw new Error('Invalid JSON value');
        }
        break;
      case 'boolean':
        convertedValue = form.value === 'true';
        break;
      case 'date':
        convertedValue = format(form.value as string, DateFormats.iso, {
          timeZone: TimeZone.RIGA,
        });
        break;
      case 'array':
        convertedValue = Array.isArray(form.value) ? form.value : [];
        break;
      case 'string':
    }

    const payload = {
      title: form.title,
      description: form.description,
      activeFrom: form.activeFrom,
      isSensitive: form.isSensitive,
      slug: form.slug,
      setting: {
        type: form.valueType,
        value: convertedValue,
        metadata: {},
      },
    };

    if (form.createNewWithActiveTime) {
      createSetting({ ...payload, status: SettingStatus.SCHEDULED });
      return;
    }

    const setting = payload.setting;

    updateSetting({ id: settingId, setting: { setting, activeFrom: payload.activeFrom } });
  };

  const handleDelete = () => {
    deleteSetting(settingId);
  };

  const handleCancel = () => {
    navigate('/dashboard/settings', { replace: true });
  };

  if (isLoadingSetting) return <div>Loading...</div>;
  if (!setting) return <div>Setting not found</div>;

  return (
    <Box sx={{ p: { xs: 2, sm: 4 } }}>
      <div className="p-4">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 24,
          }}>
          <div>
            <h1 style={{ fontSize: 32, fontWeight: 700, margin: 0, color: '#222' }}>
              Edit Settings
            </h1>
          </div>
        </div>
        <Paper
          elevation={3}
          sx={{ p: { xs: 2, sm: 4 }, maxWidth: 800, margin: '0 auto', borderRadius: 3 }}>
          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <Stack spacing={3}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={3}
                alignItems={{ xs: 'stretch', sm: 'center' }}>
                <InputLabel
                  sx={{
                    minWidth: { xs: 0, sm: 130 },
                    color: 'text.primary',
                    fontWeight: 500,
                    mb: { xs: 1, sm: 0 },
                  }}>
                  Title
                </InputLabel>
                <TextField
                  value={form.title}
                  onChange={handleChange('title')}
                  required
                  size="small"
                  sx={{ flex: 1 }}
                  fullWidth
                  disabled
                />
              </Stack>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={3}
                alignItems={{ xs: 'stretch', sm: 'center' }}>
                <InputLabel
                  sx={{
                    minWidth: { xs: 0, sm: 130 },
                    color: 'text.primary',
                    fontWeight: 500,
                    mb: { xs: 1, sm: 0 },
                  }}>
                  Description
                </InputLabel>
                <TextField
                  value={form.description}
                  onChange={handleChange('description')}
                  multiline
                  rows={2}
                  size="small"
                  sx={{ flex: 1 }}
                  fullWidth
                  disabled
                />
              </Stack>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={3}
                alignItems={{ xs: 'stretch', sm: 'center' }}>
                <InputLabel
                  sx={{
                    minWidth: { xs: 0, sm: 130 },
                    color: 'text.primary',
                    fontWeight: 500,
                    mb: { xs: 1, sm: 0 },
                  }}>
                  Active From
                </InputLabel>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DateTimePicker
                    value={form.activeFrom}
                    onChange={handleDateChange}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                    timezone={TimeZone.RIGA}
                    disabled={
                      !form.createNewWithActiveTime && setting.status !== SettingStatus.SCHEDULED
                    }
                    ampm={false}
                    minDateTime={utcStringToLocalDate(setting?.activeFrom?.toString() || '')}
                  />
                </LocalizationProvider>
              </Stack>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={3}
                alignItems={{ xs: 'stretch', sm: 'center' }}>
                <InputLabel
                  sx={{
                    minWidth: { xs: 0, sm: 130 },
                    color: 'text.primary',
                    fontWeight: 500,
                    mb: { xs: 1, sm: 0 },
                  }}>
                  Is Sensitive
                </InputLabel>
                <FormControlLabel
                  control={
                    <Switch checked={form.isSensitive} onChange={handleChange('isSensitive')} />
                  }
                  label=""
                  sx={{ m: 0 }}
                  disabled
                />
              </Stack>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={3}
                alignItems={{ xs: 'stretch', sm: 'center' }}>
                <InputLabel
                  sx={{
                    minWidth: { xs: 0, sm: 130 },
                    color: 'text.primary',
                    fontWeight: 500,
                    mb: { xs: 1, sm: 0 },
                  }}>
                  Value Type
                </InputLabel>
                <FormControl fullWidth size="small">
                  <Select
                    value={form.valueType}
                    onChange={
                      handleChange('valueType') as (
                        event: import('@mui/material/Select').SelectChangeEvent,
                      ) => void
                    }
                    required
                    disabled>
                    {valueTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              <RenderSettingFieldFromConfig
                valueType={
                  form.valueType as keyof typeof import('@components/Setting/settingFieldConfigs').default
                }
                value={form.value}
                slug={form.slug}
                onChange={(e: { target: { value: string | number | boolean | object } }) => {
                  if ('target' in e && 'value' in e.target) {
                    setForm({
                      ...form,
                      value:
                        typeof e.target.value === 'string'
                          ? String(e.target.value)
                          : e.target.value,
                    });
                  }
                }}
              />

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={3}
                alignItems={{ xs: 'stretch', sm: 'center' }}>
                <InputLabel
                  sx={{
                    minWidth: { xs: 0, sm: 130 },
                    color: 'text.primary',
                    fontWeight: 500,
                    mb: { xs: 1, sm: 0 },
                  }}>
                  Slug
                </InputLabel>
                <TextField
                  value={form.slug || ''}
                  onChange={handleChange('slug')}
                  size="small"
                  sx={{ flex: 1 }}
                  required
                  disabled
                  fullWidth
                />
              </Stack>

              {setting.status && setting.status !== SettingStatus.SCHEDULED && (
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={3}
                  alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.createNewWithActiveTime}
                        onChange={(e) =>
                          setForm({ ...form, createNewWithActiveTime: e.target.checked })
                        }
                      />
                    }
                    label="Create new scheduled setting with new active time"
                  />
                </Stack>
              )}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                justifyContent="flex-end"
                sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  sx={{ minWidth: { xs: '100%', sm: 100 }, px: 3, borderRadius: 2 }}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  sx={{ minWidth: { xs: '100%', sm: 100 }, px: 3, borderRadius: 2 }}>
                  Update
                </Button>
                {setting.status && setting.status === SettingStatus.SCHEDULED && (
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleDelete}
                    sx={{ minWidth: { xs: '100%', sm: 100 }, px: 3, borderRadius: 2 }}>
                    Delete
                  </Button>
                )}
              </Stack>
            </Stack>
          </Box>
        </Paper>
      </div>
    </Box>
  );
}

export default SettingEditPage;
